import { useCallback, useEffect, useMemo, useState } from 'react';
import { UMButton, UMFormStyles } from '../../urbanmap/_components';
import { CreationStepper, perimeterProgress, type ProgressItem, type SectionKey } from './CreationProgress';
import { HomeRail } from './HomeChrome';
import './home.css';
import { calibrateRatios, centerOf, countModifiedRatios, defaultContract, inPerimeter, nearestStation, ratiosForDegrees, stationOf, REF_DJCLIM, REF_DJU } from './domain';
import { useCreateProject } from './hooks';
import { alignToPerf } from './perf';
import { StepPerimeter, type PerimeterDraft } from './StepPerimeter';
import { StepRecap, type CopyStatus } from './StepRecap';
import { StepSettings } from './StepSettings';
import { UMR } from './tokens';
import type { DegreeDays, DjuSettings, Perimeter, RatioTable as Ratios, Referential } from './types';

interface Props {
  referential: Referential;
  onCancel: () => void;
  onOpen: (projectId: string) => void;
  flash: (message: string) => void;
}

/** Assistant de création, plein écran : périmètre → météo et ratios → récapitulatif. */
export function CreateWizard({ referential, onCancel, onOpen, flash }: Props) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<PerimeterDraft>({
    name: '', kind: 'new', networkIds: [], contract: defaultContract(),
    mode: 'communes', communes: [], shape: 'rectangle', points: [], closed: false, drawing: false,
  });
  const [stationId, setStationId] = useState(() => stationOf(referential.defaultDju).id);
  const [stationPicked, setStationPicked] = useState(false);
  const station = useMemo(() => stationOf({ stationId, reference: 0 }), [stationId]);
  /** Degrés-jours retenus pour la station : standards, corrigeables à l'étape 3. */
  const [stationDegrees, setStationDegrees] = useState<DegreeDays>(() => {
    const s = stationOf(referential.defaultDju);
    return { dju: s.dju, djclim: s.djclim };
  });
  const chooseStation = (id: string) => {
    const s = referential.stations.find((x) => x.id === id);
    if (!s) return;
    setStationId(id);
    setStationPicked(true);
    setStationDegrees({ dju: s.dju, djclim: s.djclim });
  };

  /** Degrés-jours auxquels le référentiel de ratios est établi. */
  const defaultDegrees = useMemo<DegreeDays>(() => ({ dju: REF_DJU, djclim: REF_DJCLIM }), []);
  const [myDegrees, setMyDegrees] = useState<DegreeDays>(defaultDegrees);
  /** Référentiel recalé sur ses coefficients de performance (un seul % par période, tous postes). */
  const baseRatios = useMemo(() => alignToPerf(referential.defaultRatios), [referential.defaultRatios]);
  const [myRatios, setMyRatios] = useState<Ratios>(() => alignToPerf(referential.defaultRatios));
  /** Mes ratios tels qu'appliqués au projet (snapshot validé par « Appliquer à la station »). */
  const [applied, setApplied] = useState<{ degrees: DegreeDays; ratios: Ratios }>(
    () => ({ degrees: defaultDegrees, ratios: alignToPerf(referential.defaultRatios) }),
  );
  /** Mes ratios n'alimentent le projet qu'après un premier « Appliquer à la station ». */
  const [appliedOnce, setAppliedOnce] = useState(false);

  const dju = useMemo<DjuSettings>(() => ({ stationId, reference: stationDegrees.dju }), [stationId, stationDegrees.dju]);
  /** Référentiel calibré sur les degrés-jours retenus : sert de base de comparaison. */
  const stationRatios = useMemo(() => ratiosForDegrees(stationDegrees.dju, stationDegrees.djclim), [stationDegrees]);
  /** Ratios réellement utilisés : mes ratios appliqués, calibrés sur les DJ de la station. */
  const ratios = useMemo(
    () => calibrateRatios(applied.ratios, applied.degrees, stationDegrees),
    [applied, stationDegrees],
  );
  const pending = !appliedOnce
    || countModifiedRatios(applied.ratios, myRatios) > 0
    || applied.degrees.dju !== myDegrees.dju
    || applied.degrees.djclim !== myDegrees.djclim;
  const custom = countModifiedRatios(applied.ratios, baseRatios) > 0
    || applied.degrees.dju !== defaultDegrees.dju
    || applied.degrees.djclim !== defaultDegrees.djclim;

  const [ratiosValid, setRatiosValid] = useState(true);
  const [ratiosEditing, setRatiosEditing] = useState(false);
  const [settingsVisited, setSettingsVisited] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<SectionKey, boolean>>>({});
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [progress, setProgress] = useState(0);
  const create = useCreateProject();

  const resetCopy = () => { setStatus('idle'); setProgress(0); create.reset(); };
  const patch = useCallback((p: Partial<PerimeterDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
    setStatus('idle');
    setProgress(0);
    if (p.contract) setTouched((t) => ({ ...t, contract: true }));
  }, []);
  const perimeter = useMemo<Perimeter>(
    () => (draft.mode === 'communes' ? { kind: 'communes', communes: draft.communes } : { kind: 'drawn', polygon: draft.closed ? draft.points : [] }),
    [draft.mode, draft.communes, draft.points, draft.closed],
  );
  const included = useMemo(() => referential.buildings.filter((b) => inPerimeter(b, perimeter)), [referential.buildings, perimeter]);
  const includedIds = useMemo(() => new Set(included.map((b) => b.id)), [included]);
  /** Tant que l'utilisateur n'a pas choisi, la station la plus proche du périmètre est proposée. */
  const center = useMemo(() => centerOf(included.map((b) => b.centroid)), [included]);
  useEffect(() => {
    if (stationPicked || !center) return;
    const near = nearestStation(center, referential.stations);
    setStationId(near.id);
    setStationDegrees({ dju: near.dju, djclim: near.djclim });
  }, [center, stationPicked, referential.stations]);
  const perimeterLabel = perimeter.kind === 'drawn' ? 'Zone dessinée à la main' : draft.communes.map((c) => referential.communes.find((x) => x.insee === c)?.name ?? c).join(', ');
  const networkNames = referential.networks.filter((n) => draft.networkIds.includes(n.id)).map((n) => n.name);

  useEffect(() => {
    if (status !== 'copying') return undefined;
    const t = window.setInterval(() => setProgress((p) => Math.min(create.isSuccess ? 100 : 90, p + 8)), 180);
    return () => window.clearInterval(t);
  }, [status, create.isSuccess]);
  useEffect(() => { if (status === 'copying' && progress >= 100) setStatus('done'); }, [status, progress]);

  const launch = () => {
    if (!step0Valid || !step1Valid || status === 'copying') return;
    setStatus('copying');
    setProgress(0);
    create.mutate(
      {
        name: draft.name.trim(), kind: draft.kind, networkIds: draft.networkIds, contract: draft.contract,
        perimeter, ratios, dju, usesDefaults: !custom,
      },
      { onError: () => setStatus('error') },
    );
  };

  const sections = perimeterProgress(draft, included.length, referential.stations.some((s) => s.id === stationId), touched);
  const sectionItems = Object.values(sections);
  const step0Valid = sectionItems.every((item) => item.state === 'success');
  const step1Valid = ratiosValid && !ratiosEditing && appliedOnce && !pending;
  const step0Progress: ProgressItem = {
    state: step0Valid ? 'success' : sectionItems.some((item) => item.state === 'error') ? 'error' : step === 0 ? 'current' : 'incomplete',
    detail: `${sectionItems.filter((item) => item.state === 'success').length} sur 5 rubriques validées`,
  };
  const step1Progress: ProgressItem = {
    state: !ratiosValid ? 'error' : ratiosEditing || (pending && appliedOnce) ? 'incomplete' : step1Valid ? 'success' : step === 1 ? 'current' : settingsVisited ? 'incomplete' : 'untouched',
    detail: !ratiosValid ? 'Valeurs hors plage' : ratiosEditing ? 'Édition à terminer' : step1Valid ? 'Ratios appliqués au projet' : pending && appliedOnce ? 'Modifications à appliquer' : 'Application des ratios requise',
  };
  const step2Progress: ProgressItem = {
    state: status === 'done' ? 'success' : status === 'error' ? 'error' : status === 'copying' ? 'busy' : step === 2 ? 'current' : 'untouched',
    detail: status === 'done' ? 'Projet créé' : status === 'error' ? 'Échec de la copie · réessayez' : status === 'copying' ? `${progress} %` : step === 2 ? 'Prêt à créer le projet' : 'Vérification avant copie',
  };
  const busy = status === 'copying';
  const actions = (
    <div className="pcv3-creation-actions" aria-label="Actions de création" role="group">
      {status === 'done' ? (
        <UMButton icon="arrow_forward" onClick={() => create.data && onOpen(create.data.id)}>Ouvrir le projet</UMButton>
      ) : (
        <>
          <UMButton variant="secondary" disabled={busy} onClick={onCancel}>Annuler</UMButton>
          {step > 0 && <UMButton variant="secondary" icon="arrow_back" disabled={busy} onClick={() => setStep((s) => s - 1)}>Précédent</UMButton>}
          {step === 0 && <UMButton icon="arrow_forward" disabled={!step0Valid} onClick={() => { setSettingsVisited(true); setStep(1); }}>Suivant</UMButton>}
          {step === 1 && <UMButton icon="arrow_forward" disabled={!step0Valid || !step1Valid} onClick={() => setStep(2)}>Vérifier</UMButton>}
          {step === 2 && <UMButton icon="content_copy" disabled={busy || !step0Valid || !step1Valid} onClick={launch}>{status === 'error' ? 'Réessayer' : 'Créer le projet'}</UMButton>}
        </>
      )}
    </div>
  );

  return (
    <div data-comment-view={`creation-step-${step}`} style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex', background: UMR.white, fontFamily: UMR.font, color: UMR.black }}>
      <HomeRail onHome={onCancel} flash={flash} isHome={false} disabled={busy} />
      <div className="pcv3-home-content">
      <UMFormStyles />
      <CreationStepper current={step} items={[step0Progress, step1Progress, step2Progress]} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {step === 0 && (
          <StepPerimeter
            referential={referential}
            draft={draft}
            onChange={patch}
            included={included}
            includedIds={includedIds}
            stationId={stationId}
            onStationSelect={(id) => { resetCopy(); chooseStation(id); }}
            progress={sections}
            onTouch={(key) => setTouched((t) => t[key] ? t : { ...t, [key]: true })}
            actions={actions}
          />
        )}
        {settingsVisited && (
          <div hidden={step !== 1} style={{ height: '100%' }}>
          <StepSettings
            referential={referential}
            defaultRatios={baseRatios}
            station={station}
            stationDegrees={stationDegrees}
            onStationDegreesChange={(next) => { resetCopy(); setStationDegrees(next); }}
            myDegrees={myDegrees}
            myRatios={myRatios}
            appliedDegrees={applied.degrees}
            defaultDegrees={defaultDegrees}
            onMyDegreesChange={(next) => { resetCopy(); setMyDegrees(next); }}
            onMyRatiosChange={(next) => { resetCopy(); setMyRatios(next); }}
            projectRatios={ratios}
            stationRatios={stationRatios}
            pending={pending}
            applied={appliedOnce}
            onApply={() => { resetCopy(); setApplied({ degrees: myDegrees, ratios: myRatios }); setAppliedOnce(true); }}
            onRatiosValidity={setRatiosValid}
            onEditingChange={(editing) => { resetCopy(); setRatiosEditing(editing); }}
            actions={step === 1 ? actions : null}
          />
          </div>
        )}
        {step === 2 && (
          <StepRecap
            name={draft.name.trim()}
            kind={draft.kind}
            networkNames={networkNames}
            contract={draft.contract}
            perimeterLabel={perimeterLabel}
            buildingCount={included.length}
            stationName={stationOf(dju).name}
            dju={stationDegrees.dju}
            djclim={stationDegrees.djclim}
            degreesEdited={stationDegrees.dju !== station.dju || stationDegrees.djclim !== station.djclim}
            status={status}
            progress={progress}
            actions={actions}
          />
        )}
      </div>

      </div>
    </div>
  );
}
